import * as React from 'react';
import { withOpenState, OpenStateInjectedProps } from 'ui/common/enhancers';
import { AddDocumentItemOptions, DeleteDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import { keyCodeTypes } from 'ui/common/key-code-types';
import * as navigate from 'app/area/shared/navigation';
import { serializeConnectionState } from 'app/area/case/draft/connected-state';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';
import { ButtonPrimary, AddButton, Numberbox, ButtonTertiary } from 'ui';
import { MdAdd } from 'react-icons/md';
import get from 'lodash/get';
import styles from './connect-case-selector.scss';
import { RenderMode, StoreField } from 'app/types-shared';
import { tableName, columnName } from 'app/area/case/case-data-model';
import { Chain } from 'ui/common/svg-icons/chain';
import { CancelablePromise, makeCancelable } from 'app/infrastructure/js/cancelable-promise';
import { PrimaryActionComponentProps } from 'app/area/case-report/case-listing/types';
import { nextSequenceValue } from 'app/area/case/features/connected-cases/sequence';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import debounce from 'lodash/debounce';
import { FavouriteInstance } from 'app/area/favourite/favourite-context';
import { ModalSize } from 'app/components/constants';

const debounceTime = 500;

export interface ConnectCaseSelectorProps {
  entityId: number;
  caseId: number;
  connectionTypeId: number;
  maxSequence: number;
  textAddExisting: string;
  textAddNew: string;
  textNoConnectionToItself: string;
  textConnect: string;
  textDisconnect: string;
  placeholderSearch: string;
  titleSearch: string;
  textInvalidRangeError: string;
  readOnly?: boolean;
  required?: boolean;
  stateForCaseIds: {[caseId: string]: { valid: boolean; fetching: boolean }};
  validateCaseId: (caseId: number) => void;
  onAddDocumentItem: (selectAddedItem: boolean, options: AddDocumentItemOptions) => void;
  onDeleteDocumentItem: (selectParentItem: boolean, options: DeleteDocumentItemOptions) => void;
  createSearchForCasesToConnect: () => Promise<number>;
  locale?: string;
  formatNumber?: (n: number, options?: Intl.NumberFormatOptions) => string;
  connectedDrafts?: any[];
  searchEnabled?: boolean;
  reportId?: number;
  connectedCasesIds?: number[];
  connectedCasesPendingDeleteIds?: number[];
  favouriteComponent: React.ComponentType<any>;
  defaultOpen?: boolean;
  hideButton?: boolean;
  onCloseCallback?: () => void;
  disableButton?: boolean;
}

interface ConnectCaseSelectorState {
  caseIdToConnect: number;
  favouriteId?: number;
}

class ConnectCaseSelectorComponent
  extends React.PureComponent<ConnectCaseSelectorProps & OpenStateInjectedProps, ConnectCaseSelectorState> {
  private keyMap: KeyMap;
  private handlers: Handlers;
  private loadPromise: CancelablePromise<number>;

  public constructor(props: ConnectCaseSelectorProps & OpenStateInjectedProps) {
    super(props);
    this.state = {
      caseIdToConnect: undefined
    };

    this.keyMap = {
      onEnter: [keyCodeTypes.Enter]
    };
    this.handlers = {
      onEnter: (event) => this.onEnter(event)
    };
  }

  public componentDidMount() {
    const { defaultOpen } = this.props;
    if (defaultOpen) {
     this.openCasesToConnect();
    }
  }

  public componentWillUnmount() {
    if (this.loadPromise) {
      this.loadPromise.cancel();
      this.loadPromise = undefined;
    }
  }

  private onEnter = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event) {
      event.preventDefault();
    }
    this.addConnection();
  };

  private closeDialog = () => {
    const {close} = this.props;

    close(() => {
      this.setState({
          ...this.state,
          ...{
            caseIdToConnect: undefined,
            favouriteId: undefined
          }
        });
      });

    if (this.props.onCloseCallback) {
      this.props.onCloseCallback();
    }
  };

  private getStateForCaseId(): { valid?: boolean; fetching?: boolean } {
    return get(this.props, `stateForCaseIds[${this.state.caseIdToConnect}]`) || {};
  }

  private caseIdChanged: (caseIdToConnect: number) => void = debounce((caseIdToConnect: number) => {
    this.setState({
      ...this.state,
      ...{ caseIdToConnect }
    }, () => {
      const validStateForCaseId = this.getStateForCaseId();
      if(this.state.caseIdToConnect > 0 && !validStateForCaseId.fetching) {
        this.props.validateCaseId(this.state.caseIdToConnect);
      }
    });
  }, debounceTime);

  private getNextSequenceValue = () => nextSequenceValue(this.props.maxSequence);

  private addConnection = () => {
    const { onAddDocumentItem } = this.props;

    const validStateForCaseId = this.getStateForCaseId();

    if (onAddDocumentItem && validStateForCaseId.valid && !validStateForCaseId.fetching) {
      onAddDocumentItem(true, {
        childId: this.state.caseIdToConnect,
        initialValues: {
          [columnName[tableName.connectedCases].connectedSequence]: [{ value: this.getNextSequenceValue() }] as StoreField[]
        }
      });
      this.closeDialog();
    }
  };

  private openCasesToConnect = async () => {
    this.props.open();
    try {
      this.loadPromise = makeCancelable(this.props.createSearchForCasesToConnect());
      const favouriteId = await this.loadPromise.promise;
      this.setState({
        favouriteId
      });
      this.loadPromise = undefined;
    }
    catch (e) {
      if (e && e.isCanceled) {
        return;
      }
    }
  };

  private addConnectionToNewCase = () => {
    const {entityId: draftId, connectionTypeId, connectedDrafts = []} = this.props;

    navigate.toSelectConnected(
      serializeConnectionState([
        {draftId, connectionTypeId, sequence: this.getNextSequenceValue()},
        ...connectedDrafts
      ])
    );
  };

  private renderActionComponent = (props: PrimaryActionComponentProps) => {
    const { textConnect, textDisconnect, connectedCasesIds, connectedCasesPendingDeleteIds, disableButton } = this.props;

    const caseAlreadyConnected = connectedCasesIds && connectedCasesIds.indexOf(props.caseId) > -1;
    const pendingDelete = connectedCasesPendingDeleteIds && connectedCasesPendingDeleteIds.indexOf(props.caseId) > -1;

    return caseAlreadyConnected ?
      <ButtonTertiary onClick={this.removeConnection(props.caseId)} disabled={disableButton}>{textDisconnect}</ButtonTertiary> :
      <ButtonPrimary onClick={this.addConnectionByCaseId(props.caseId)} disabled={pendingDelete || disableButton}>{textConnect}
      </ButtonPrimary>;
  };

  private addConnectionByCaseId = (caseIdToConnect: number) => () => {
    const { onAddDocumentItem } = this.props;

    onAddDocumentItem(false, {
      childId: caseIdToConnect,
      initialValues: {
        [columnName[tableName.connectedCases].connectedSequence]: [{ value: this.getNextSequenceValue() }] as StoreField[]
      }
    });
  };

  private removeConnection = (caseIdToRemove: number) => () =>  {
    const { entityId, onDeleteDocumentItem, connectionTypeId } = this.props;
    onDeleteDocumentItem && onDeleteDocumentItem(
      false,
      {
        entityType: tableName.connectedCases,
        parentEntityPath: [
          {[tableName.trans]: entityId},
          {[tableName.connectionTypes]: connectionTypeId}
        ],
        childId: caseIdToRemove
      });
  };

  private renderSelectDialog() {
    const { caseId, placeholderSearch, textAddExisting, textNoConnectionToItself, titleSearch,
            textInvalidRangeError, formatNumber, locale, isClosing, searchEnabled,
            favouriteComponent: FavouriteComponent, disableButton } = this.props;
    const { caseIdToConnect } = this.state;
    const { inputWrapper, inputSearch } = styles;

    const entityForCaseId = this.getStateForCaseId();
    const fetchingEntityId = entityForCaseId.fetching;
    const unknownEntityId = !entityForCaseId.valid && !fetchingEntityId;
    const sameCaseId = caseIdToConnect && caseId === caseIdToConnect;

    const addButtonText = sameCaseId ? textNoConnectionToItself : textAddExisting;
    const closingAnimation = isClosing ? 'animate-out' : '';
      
    return (
      <div className={`${closingAnimation} connect-case-selector` }>
      <ModalWrapper {...this.props}
          headerTitle={titleSearch}
          onClose={this.closeDialog}
          size={ModalSize.large}
        >
          <HotKeys keyMap={this.keyMap} handlers={this.handlers}>
            <div className={inputWrapper}>
              <Numberbox
                componentId={'caseid-input'}
                className={`${inputSearch}`}
                placeholder={placeholderSearch}
                onChange={this.caseIdChanged}
                value={caseIdToConnect}
                formatNumber={formatNumber}
                locale={locale}
                useThousand={false}
                textInvalidRangeError={textInvalidRangeError}
              />
              <span className="input-group-btn">
                <ButtonPrimary
                  onClick={this.addConnection}
                  disabled={caseIdToConnect <= 0 || fetchingEntityId || unknownEntityId || sameCaseId || disableButton}>
                  {addButtonText}
                </ButtonPrimary>
              </span>
            </div>
            {searchEnabled && this.state.favouriteId &&
              <FavouriteInstance hideHeader={true} hideVisualizationOptions={true}>
                <FavouriteComponent
                  favouriteId={this.state.favouriteId}
                  embeddedFavourite={true}
                  caseReportExternalProps={{
                    primaryActionComponentFactory: this.renderActionComponent,
                    requestedRenderMode: RenderMode.embeddedReport,
                    headerVisibilityConfiguration: {},
                    windowAsScrollableAncestor: false
                  }}
                  {...this.props} />
              </FavouriteInstance>}
          </HotKeys>
        </ModalWrapper>
      </div>
    );
  }

  public render() {
    const {
      readOnly,
      required,
      isOpen,
      textAddExisting,
      textAddNew,
      hideButton
    } = this.props;

    const {
      connectCaseSelector, addButton
    } = styles;

    if (readOnly) {
      return null;
    }

    const addButtons =
          <div className={addButton}>
            <AddButton icon={MdAdd} onClick={this.addConnectionToNewCase} required={required}>{textAddNew}</AddButton>
            <AddButton icon={Chain} onClick={this.openCasesToConnect} required={required}>{textAddExisting}</AddButton>
          </div>;

    return (
      <div className={connectCaseSelector}>
        {!hideButton && addButtons}
        {isOpen && this.renderSelectDialog()}
      </div>
    );
  }
}

const c = withOpenState(true)(ConnectCaseSelectorComponent);

export { c as ConnectCaseSelector };
